import React, { useState } from "react";
import { Settings, ExternalLink, CheckCircle, Send, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { sendEmail } from "@/lib/emailService";

export default function EmailJsSetup({ settings, onSave, showToast }) {
  const [cfg, setCfg] = useState({ ...settings });
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const update = (key, value) => setCfg((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => onSave(cfg);

  const handleTestEmail = async () => {
    if (!testEmail || !cfg.EMAILJS_PUBLIC_KEY || !cfg.EMAILJS_SERVICE_ID || !cfg.VISITOR_TEMPLATE_ID) {
      showToast("Please fill in EmailJS credentials first", "error");
      return;
    }
    // Save settings first so they persist
    onSave(cfg);
    setTesting(true);
    try {
      const result = await sendEmail(
        cfg.EMAILJS_SERVICE_ID,
        cfg.VISITOR_TEMPLATE_ID,
        {
          from_name: "Test User",
          from_email: testEmail,
          to_email: testEmail,
          company: "Test Company",
          phone: "+1234567890",
          service: "Test Service",
          message: "This is a test email from ElitePlus admin panel.",
          reference_id: "TEST-" + Date.now(),
          submission_date: new Date().toLocaleDateString(),
        },
        cfg.EMAILJS_PUBLIC_KEY
      );
      showToast(`Test email sent to ${testEmail} (Status: ${result.status})`, "success");
    } catch (err) {
      let errMsg = "Check EmailJS credentials and try again";
      try {
        if (typeof err === "string") errMsg = err;
        else if (err?.status) errMsg = `EmailJS error (status ${err.status})`;
        else if (err?.message) errMsg = err.message;
      } catch {}
      showToast(errMsg, "error");
    } finally {
      setTesting(false);
    }
  };

  const isConfigured = cfg.EMAILJS_PUBLIC_KEY && cfg.EMAILJS_SERVICE_ID && cfg.VISITOR_TEMPLATE_ID;

  return (
    <div className="space-y-6">
      {/* Status */}
      <div className={`p-4 rounded-xl flex items-center gap-3 ${isConfigured ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`}>
        {isConfigured ? (
          <><CheckCircle className="w-5 h-5 text-emerald-500" /><span className="text-emerald-700 text-sm font-medium">EmailJS is configured and ready</span></>
        ) : (
          <><AlertCircle className="w-5 h-5 text-amber-500" /><span className="text-amber-700 text-sm font-medium">EmailJS credentials required — follow the setup guide below</span></>
        )}
      </div>

      {/* Setup Guide */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
            <Settings className="w-5 h-5 text-cyan-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">EmailJS Setup Guide</h2>
            <p className="text-sm text-slate-500">One-time setup to enable email sending</p>
          </div>
        </div>

        <ol className="space-y-3 mb-6 text-sm text-slate-600">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
            <span>Create a free account at <a href="https://www.emailjs.com/signup" target="_blank" rel="noopener noreferrer" className="text-cyan-600 underline font-medium">emailjs.com<ExternalLink className="w-3 h-3 inline ml-1" /></a> (200 emails/month free)</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
            <span>Go to <strong>Email Services</strong> → Add your Hostinger SMTP (smtp.hostinger.com, port 465, SSL) → Copy the <strong>Service ID</strong></span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
            <span>Go to <strong>Email Templates</strong> → Create a template for visitor acknowledgment → Copy the <strong>Template ID</strong></span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
            <span>Create another template for internal notification → Copy the <strong>Template ID</strong></span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">5</span>
            <span>Go to <strong>Account</strong> → <strong>API Keys</strong> → Copy your <strong>Public Key</strong></span>
          </li>
        </ol>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-slate-700">EmailJS Public Key *</Label>
            <Input data-testid="emailjs-public-key" value={cfg.EMAILJS_PUBLIC_KEY || ""} onChange={(e) => update("EMAILJS_PUBLIC_KEY", e.target.value)} placeholder="Paste your Public Key from EmailJS" className="bg-slate-50 border-slate-200" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700">EmailJS Service ID *</Label>
            <Input data-testid="emailjs-service-id" value={cfg.EMAILJS_SERVICE_ID || ""} onChange={(e) => update("EMAILJS_SERVICE_ID", e.target.value)} placeholder="e.g. service_abc123" className="bg-slate-50 border-slate-200" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-slate-700">Visitor Template ID *</Label>
              <Input data-testid="emailjs-visitor-template" value={cfg.VISITOR_TEMPLATE_ID || ""} onChange={(e) => update("VISITOR_TEMPLATE_ID", e.target.value)} placeholder="e.g. template_visitor" className="bg-slate-50 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Internal Template ID *</Label>
              <Input data-testid="emailjs-internal-template" value={cfg.INTERNAL_TEMPLATE_ID || ""} onChange={(e) => update("INTERNAL_TEMPLATE_ID", e.target.value)} placeholder="e.g. template_internal" className="bg-slate-50 border-slate-200" />
            </div>
          </div>
        </div>
      </div>

      {/* Feature Toggles */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-5">Feature Toggles</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div>
              <Label className="text-slate-700 font-medium">Enable Email Sending</Label>
              <p className="text-xs text-slate-500 mt-1">Master switch for all contact form emails</p>
            </div>
            <Switch data-testid="email-enabled-toggle" checked={cfg.emailEnabled !== false} onCheckedChange={(v) => update("emailEnabled", v)} />
          </div>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div>
              <Label className="text-slate-700 font-medium">Visitor Acknowledgment Email</Label>
              <p className="text-xs text-slate-500 mt-1">Auto-reply sent to the visitor</p>
            </div>
            <Switch data-testid="visitor-email-toggle" checked={cfg.visitorEmailEnabled !== false} onCheckedChange={(v) => update("visitorEmailEnabled", v)} />
          </div>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div>
              <Label className="text-slate-700 font-medium">Internal Notification Email</Label>
              <p className="text-xs text-slate-500 mt-1">Alert sent to your team</p>
            </div>
            <Switch data-testid="internal-email-toggle" checked={cfg.internalEmailEnabled !== false} onCheckedChange={(v) => update("internalEmailEnabled", v)} />
          </div>
        </div>
      </div>

      {/* Test Email */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center">
            <Send className="w-5 h-5 text-teal-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Send Test Email</h2>
            <p className="text-sm text-slate-500">Verify your EmailJS setup is working</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Input data-testid="test-email-input" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your@email.com" className="bg-slate-50 border-slate-200" />
          <Button data-testid="send-test-email-btn" onClick={handleTestEmail} disabled={testing || !testEmail} className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white whitespace-nowrap">
            {testing ? "Sending..." : "Send Test"}
          </Button>
        </div>
      </div>

      <div className="flex justify-end">
        <Button data-testid="save-setup-btn" onClick={handleSave} className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold px-8">
          Save Settings
        </Button>
      </div>
    </div>
  );
}
